import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Rocket, Search, Wrench, ArrowRight, Clock, ShoppingCart, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { ShareButtons } from "../components/ShareButtons";
import { CTASection } from "../components/CTASection";
import { GrowthCTA } from "../components/GrowthCTA";
import { mdToHtml } from "../lib/md";
import { VlogPlayer } from "../components/VlogPlayer";
import { useSeo, SITE_URL } from "../lib/seo";

const tools = [
  { to: "/seo-tools", icon: Search, t: "Free SEO Analyzer", d: "Check your domain authority, speed & rank." },
  { to: "/services/marketing", icon: Rocket, t: "Ad Management", d: "Rookie, Startup & To The Moon plans." },
  { to: "/services/computer-repair", icon: Wrench, t: "Computer Repair", d: "Fast, affordable device repair." },
];

export default function BlogPost() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/article-by-path`, { params: { path: pathname } })
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [pathname, slug]);

  const a = data?.article;
  const canonicalPath = a ? (a.legacy_path || `/blog/${a.slug}`) : pathname;
  const desc = a ? (a.seo_description || a.excerpt || "").replace(/…$/, "") : "";
  useSeo(a ? {
    title: `${a.title} | San Antonio's Web Design`,
    description: desc,
    path: canonicalPath,
    image: a.cover,
    type: "article",
    noindex: !!a.noindex,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "headline": a.title,
          "description": desc,
          "image": a.cover,
          "datePublished": a.created_at,
          "dateModified": a.created_at,
          "author": { "@type": "Organization", "name": a.author || "San Antonio's Web Design" },
          "publisher": {
            "@type": "Organization",
            "name": "San Antonio's Web Design",
            "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo.png` },
          },
          "mainEntityOfPage": { "@type": "WebPage", "@id": SITE_URL + canonicalPath },
          "wordCount": a.word_count,
          "articleSection": a.tags?.[1] || "Article",
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL + "/" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": SITE_URL + "/blog" },
            { "@type": "ListItem", "position": 3, "name": a.title, "item": SITE_URL + canonicalPath },
          ],
        },
      ],
    },
  } : {});

  if (notFound) return <div className="mx-auto max-w-4xl px-6 py-24 text-center text-muted-foreground" data-testid="post-not-found">Post not found.</div>;
  if (!data) return <div className="mx-auto max-w-4xl px-6 py-24 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-16" data-testid="blogpost-page">
      <div className="grid lg:grid-cols-[1fr_320px] gap-12">
        <article>
          <img src={a.cover} alt={a.title} className="w-full aspect-[21/9] object-cover rounded-3xl" />
          <div className="flex items-center gap-3 mt-6 text-sm text-muted-foreground">
            <span className="rounded-full bg-primary/10 text-primary px-3 py-1 font-semibold">{a.tags?.[1] || "Article"}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {Math.max(1, Math.round(a.word_count / 220))} min read</span>
          </div>
          <h1 className="font-head text-3xl md:text-4xl font-black mt-4 leading-tight">{a.title}</h1>
          <div className="flex items-center justify-between gap-4 mt-4 mb-6 flex-wrap">
            <span className="text-sm text-muted-foreground">By {a.author}</span>
            <ShareButtons title={a.title} compact />
          </div>
          <Link to="/get-started" data-testid="post-order-now" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 h-11 font-semibold hover:brightness-110 transition-[filter] mb-8"><Rocket className="h-4 w-4" /> Order Now — from $299</Link>
          <VlogPlayer slug={a.slug} fallbackCover={a.cover} />
          <div className="prose-sawd" dangerouslySetInnerHTML={{ __html: mdToHtml(a.body) }} />

          <GrowthCTA />

          {data.related?.length > 0 && (
            <div className="mt-14">
              <h2 className="font-head text-2xl font-extrabold mb-5">Related Articles</h2>
              <div className="grid gap-4 sm:grid-cols-3" data-testid="related-articles">
                {data.related.map((r) => (
                  <Link key={r.slug} to={r.legacy_path || `/blog/${r.slug}`} className="group rounded-2xl glass p-4 hover:-translate-y-1 transition-transform">
                    <h3 className="font-head font-bold text-sm group-hover:text-primary transition-colors line-clamp-3">{r.title}</h3>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs text-primary font-semibold">Read <ArrowRight className="h-3 w-3" /></span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>

        <aside className="space-y-4 lg:sticky lg:top-24 h-fit" data-testid="post-sidebar">
          {a.product_slug && (
            <div className="rounded-2xl glass p-6" data-testid="post-product-cta">
              <img src={a.cover} alt={a.title} className="w-full aspect-square object-cover rounded-xl mb-4" />
              <p className="text-2xl font-head font-black text-primary">${a.product_price?.toLocaleString?.(undefined, { minimumFractionDigits: 2 })}</p>
              <p className="text-sm text-muted-foreground">{a.product_rating}★ rated · in stock</p>
              <a data-testid="post-buy-amazon" href={a.product_url} target="_blank" rel="noopener noreferrer"
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground h-11 font-semibold hover:brightness-110 transition-[filter]">
                Buy on Amazon <ExternalLink className="h-4 w-4" />
              </a>
              <Link to={`/shop/${a.product_slug}`} className="mt-2 flex items-center justify-center gap-2 rounded-full border border-primary text-primary h-11 font-semibold hover:bg-primary/10 transition-colors">
                <ShoppingCart className="h-4 w-4" /> View in Shop
              </Link>
            </div>
          )}
          <div className="rounded-2xl glass p-6">
            <h3 className="font-head font-bold text-lg">Grow with us</h3>
            <p className="text-sm text-muted-foreground mt-1">Try our free tools & services.</p>
            <div className="mt-4 space-y-3">
              {tools.map((t) => (
                <Link key={t.to} to={t.to} className="group flex gap-3 items-start rounded-xl p-3 hover:bg-secondary transition-colors">
                  <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><t.icon className="h-4 w-4" /></span>
                  <span>
                    <span className="block font-semibold text-sm group-hover:text-primary transition-colors">{t.t}</span>
                    <span className="block text-xs text-muted-foreground">{t.d}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
          <Link to="/get-started" className="block text-center rounded-2xl bg-primary text-primary-foreground p-6 font-head font-bold hover:brightness-110 transition-[filter]">
            Order Now — from $299
          </Link>
        </aside>
      </div>
      <CTASection />
    </div>
  );
}
